import { sendGaEvent } from "../../utils/analytics/google";
import { currencyFormatter , formatCurrencyForGa, getPriceValue } from '../../utils/currency';
import { config } from '../../utils/config'
import { dispatchGaAndFacebookEvent } from "../../utils/analytics";
import { ReOrder } from "../../enums/reOrder";
import { get as getStorage, set as setStorage } from '../../utils/storage/localStorage';
import { CustomizeLater } from "../../enums/customizeLater";
import { UserSelectionInfo } from "../../models/userSelection";
import { calculateThresholdStatus, hasLineItem } from "../../utils/cart";
import { get as getCookie } from "../../utils/cookies";
import Cart from "../../models/cart";

declare const window: any;

/**
 * Common utility function to get decimal value for GA events
 * Handles currency formatting and decimal conversion with consistent logic
 */
export const getDecimalValueForGa = (value: number, fractionDigits: number): string => {
    if (!value) return "0";
    
    const formattedValue = window.np.getPriceValue(currencyFormatter(value, fractionDigits)).replace(/ /g, "");
    return formatCurrencyForGa(formattedValue || 0, fractionDigits);
};

/**
 * Helper function to determine coupon value for GA events
 * Handles both product-level and cart-level discount codes
 */
export const getCouponValue = (item: any, cart: any): string => {
    const data = item?.data;
    if (cart.productDiscountExist && data?.productDiscountCode) {
        return data.productDiscountCode;
    }
    if (data?.totalDiscount > 0 && (cart.cartDiscountExist || (data?.totalDiscount && cart.validDiscountCode))) {
        return cart.validDiscountCode;
    }
    return "NA";
};

/**
 * Generic helper function to format price values for GA events
 * Handles currency formatting and removes spaces - used for prices, discounts, charges, etc.
 */
export const formatPriceForGa = (priceValue: number, fractionDigits: number): string => {
    return window.np.getPriceValue(currencyFormatter(priceValue, fractionDigits)).replace(/ /g,"");
};

export const getSliderInfoFromLS = (lineItemId: string) => {
    if (!window.localStorage) return;

    const gaSlideInfo = getStorage('qty-select-type');
    if (!gaSlideInfo || !Object.keys(gaSlideInfo).length || !gaSlideInfo[lineItemId]) return {};
    return {
        qty_select_type: gaSlideInfo[lineItemId]['qtySelectType']
    }
}

export const attributeKeyMapping: { [key: string]: string } = {
    'Color Name': 'prd_col',
    'Barrel Color': 'br_col',
    'Ink Color': 'in_col',
    'Trim Color': 'tr_col',
    'Product Color': 'prd_col',
    'Product 1 Color': 'prd_col',
    'Cap Color': 'cap_col',
    'Clip Color': 'clp_col',
    'Grip Color': 'gr_col',
    'Stylus Color': 'sty_col',
    'Handle Color': 'han_col',
    'Year': 'yr',
    'Product Options': 'prd_opt',
    'Calendar Language': 'cal_lang',
    'Highlighter Color':'hi_col',
  };

  export const getCurrentLineItemId = (source, x, i) => {
    if (source?.lineItems[x][i]?.type === 'mainitem') {
        return source?.lineItems[x][i]?.data?.id || "";
    } else if (source?.lineItems[x][i]?.type === 'upsell') {
        return source?.lineItems[x][i]?.data?.mainItemId || "";
    }
    return "";
};

export const getCstmzrTypeInfoFromLS = (mainItemId, isEventLevel = false) => {
    if (!window.localStorage || !mainItemId) return {};
    const gaInfoKey = "__gaInfo__";

    let gaInfo = getStorage(gaInfoKey)?.[mainItemId];

    if (gaInfo && isEventLevel) {
        const { final_color_cont, default_color_cont, edit_color, ...filteredGaInfo } = gaInfo;

        // Check if edit_color value starts with # and modify its value accordingly
        if (edit_color && edit_color.startsWith('#')) {
            filteredGaInfo.edit_color = 'yes';
        } else if (edit_color) {
            filteredGaInfo.edit_color = edit_color;
        }

        gaInfo = filteredGaInfo;
    }

    return gaInfo ? gaInfo : {};
};

export const setAttributes = (userSelectionInfo: UserSelectionInfo[],isProductOOS?:boolean,lineItemId?:string,templateKey: string = ''): any[] => {
    const attributes: any[] = [];
    if(templateKey !== ''){
        attributes.push({
            key: 'temp_code',
            value: templateKey
        });
    }
    const removedLineItems = getCookie('np.isLineItemsRemoved') || '[]';
    const parsedArray = JSON.parse(removedLineItems);

    // Find the matching lineItem and get its SIZE values
    const matchedItem = parsedArray.find((item: any) => item.lineItemId === lineItemId);
    const oosSizes = matchedItem?.SIZE?.split("|") || [];

    for (const info of userSelectionInfo) {
        if (info?.multiDisplayValue) {
            info.multiDisplayValue.forEach(val => {
                const [key, value] = val.split("|");
                // If the key includes any OOS size, set value as 'OOS'
                attributes.push({
                    key,
                    value: (oosSizes.includes(key) || isProductOOS) ? "OOS" : value,
                });
            });
        } else if (info?.originalAttributeKey && info?.originalAttributeValue) {
            const mappedKey = attributeKeyMapping[info.originalAttributeKey] || info.originalAttributeKey;
            attributes.push({
                key: mappedKey,
                value: isProductOOS ? `${info.originalAttributeValue}_OOS` : info.originalAttributeValue
            });
        }
    }

    return attributes;
}


const setSliderInfoToLS = (lineItemId, action) => {
    if (!window.localStorage) return;

    let gaSlideInfo = getStorage('qty-select-type');
    if (!gaSlideInfo || !Object.keys(gaSlideInfo).length || !gaSlideInfo[lineItemId]) return;

    gaSlideInfo[lineItemId]['qtySelectType'] = action;
    setStorage('qty-select-type', gaSlideInfo);
}
const unsetSliderInfoToLS = (lineItemId)=>{
    if (!window.localStorage) return;

    let gaSlideInfo = getStorage('qty-select-type');
    if (!gaSlideInfo || !Object.keys(gaSlideInfo).length || !gaSlideInfo[lineItemId]) return;

    delete gaSlideInfo[lineItemId];
    setStorage('qty-select-type', gaSlideInfo);
}

export const onProceedToCheckoutOperation = (cart, currentPage,coupon,isShippingDisc,isPaypalCheckout = false) => {
    let products: any = [];
    try {
        const { isThresholdMet } = calculateThresholdStatus(cart, coupon?.thresholdValue);
        const doesDiscountExist = (cart?.discountExists || (!cart?.shippingCustomItem && coupon?.thresholdValue && isThresholdMet && isShippingDisc) || (!cart?.shippingCustomItem && !coupon?.thresholdValue && isShippingDisc)) ?? false;
        let cartTotalSaving = cart.customerDiscountCoupon;
        let shipping = currencyFormatter(cart?.totalDeliveryChargeExclVat, cart?.fractionDigits);
        let tax = config('isNASite') ? currencyFormatter(0, cart.fractionDigits) : currencyFormatter(cart.VAT, cart.fractionDigits);
        for (let x of Object.keys(cart.lineItems)) {
            for (let i = 0; i < cart.lineItems[x].length; i++) { 
                if (cart.lineItems[x][i].type !== 'free' && cart.lineItems[x][i].type !== 'subitem' && cart.lineItems[x][i].type !== 'additionalcharge') {
                    let attributes: any = [];
                    if (cart.lineItems[x][i].type !== 'accessory') {
                        const userSelectionInfo = cart?.lineItems[x]?.[0]?.data?.productAttributes?.userSelectionInfo || [];
                        const isProductOOS = cart?.lineItems[x]?.[0]?.data?.isOutOfStock || false;
                        let currentlineItemId = getCurrentLineItemId(cart, x, i);
                        const templateKey = cart?.lineItems[x]?.[0]?.data?.customfield?.templateKey || '';
                        attributes = setAttributes(userSelectionInfo,isProductOOS,currentlineItemId,templateKey);
                    }
                    let product : any = {
                        accessories: cart.lineItems[x][i].type === 'accessory' ? 1 : 0,
                        attributes: attributes,//lineitem.productAttributes.userSelectionInfo.[originalAttributeValue,originalAttributeKey]
                        class: cart.lineItems[x][i].type === 'accessory' ? cart.lineItems[x][i].data.productFamilyName : cart.lineItems[x][0].data.webCode,//lineitem.webCode
                        logo: (cart.lineItems[x][i].type === 'upsell' || cart.lineItems[x][i].type === 'accessory') ? 0 : window.np.getPriceValue(currencyFormatter(cart.lineItems[x][0].data.itemLogoCharge, cart.fractionDigits)).replace(/ /g,""),//lineitem.itemLogoCharge
                        name: cart.lineItems[x][i].type === 'upsell' ? cart.lineItems[x][0].data.name : cart.lineItems[x][i].data.name,//lineitem.name
                        product_type: (cart.lineItems[x][i].type === 'upsell' || cart.lineItems[x][i].type === 'accessory') ? cart.lineItems[x][0].data.productType : cart.lineItems[x][i].data.productType,
                        price: window.np.getPriceValue(currencyFormatter(cart.lineItems[x][i].data.unitPrice, cart.fractionDigits)).replace(/ /g,""),
                        qty: cart.lineItems[x][i].type === 'mainitem' ? cart.lineItems[x][i].data.totalQuantityWithSubItem : cart.lineItems[x][i].data.quantity,//lineitem.quantity totalQuantityWithSubItem
                        qty_free: cart?.lineItems[x][0]?.data?.free_qty ? cart.lineItems[x][0].data.free_qty : 0,
                        setup: (cart.lineItems[x][i].type === 'upsell' || cart.lineItems[x][i].type === 'accessory') ? 0 : window.np.getPriceValue(currencyFormatter(cart.lineItems[x][0].data.lineItemSetupCharge,cart.fractionDigits)).replace(/ /g,""),//lineitem.lineItemSetupCharge
                        sku: cart.lineItems[x][0].data.mcpSku,//lineitem.mcpSku
                        upsell: cart.lineItems[x][i].type === 'upsell' ? 1 : 0,
                        ...addPathAndPosition(cart.lineItems[x][i]?.data?.id),
                        ...addMerchantCategory(cart.lineItems[x][i]?.data?.id),
                        ...addProofAndCommentForUsg(cart?.lineItems[x][i]),
                        ...getSliderInfoFromLS(cart.lineItems[x][i]?.data?.id),
                        ...addPCCFlag(cart?.lineItems[x][i]?.data?.postCheckOutCustomization),
                        ...addImportFeeForGa(cart?.lineItems[x][i], cart?.fractionDigits)
                    };
                
                    const currentItem = cart?.lineItems[x][i];
                    const itemData = currentItem?.data;
                    const itemType = currentItem?.type;
                    product.coupon = getCouponValue(currentItem, cart);

                    switch (itemType) {
                        case 'mainitem':
                            product = {...product, ...getCstmzrTypeInfoFromLS(itemData?.id)};
                            product.reorder = itemData?.customfield?.lineItemOrderType === ReOrder.REORDER_LINEITEM_ORDERTYPE ? 1 : 0;
                            product.original_price = getDecimalValueForGa(cart.lineItems[x][0].data.baseUnitPrice || 0, cart?.fractionDigits);
                            product.discount = Number(((itemData.totalDiscountWithSubItem / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits));
                            break;
                        case 'upsell':
                            product = {...product, ...getCstmzrTypeInfoFromLS(itemData?.mainItemId)};
                            product.discount = Number(((itemData.totalDiscount / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits));
                            break;
                        case 'accessory':
                            product.discount = Number(((itemData.totalDiscount / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits));
                            break;
                        default:
                            break;
                    }

                    product['logo_dec'] = formatCurrencyForGa(product?.logo || 0, cart?.fractionDigits);
                    product['setup_dec'] = formatCurrencyForGa(product?.setup || 0, cart?.fractionDigits);
                    product['price'] = formatCurrencyForGa(product?.price || 0, cart?.fractionDigits);
                    products.push(product)
                }
            }
        }
        let revenueWithShipping = currencyFormatter(cart.cartSubtotal + (cart?.totalDeliveryChargeExclVat || 0), cart.fractionDigits);
        let _metaData = {
            'event': 'checkout',
            'checkout_step': 1,
            'checkout_type': isPaypalCheckout ? 'paypal checkout' : 'national pen checkout',
            'step_name': 'Cart',
            'products': products,
            'revenue': window.np.getPriceValue(revenueWithShipping).replace(/ /g,""),
            'coupon': (doesDiscountExist && cartTotalSaving === coupon.code) ? cartTotalSaving : "NA",
            'tax': window.np.getPriceValue(tax).replace(/ /g,"") || 0,
            ...addPCCFlag(cart?.hasPCCItem),
            ...getTotalImportFeeForGa(cart),
            'shipping': window.np.getPriceValue(shipping).replace(/ /g,"") || 0,
            'orderDiscount': cart?.cartTotalSaving ? 
                getPriceValue(
                    currencyFormatter(cart?.cartTotalSaving, cart?.fractionDigits)
                ).replace(/ /g,"") : 0,        
        };

        _metaData['tax_dec'] = formatCurrencyForGa(_metaData.tax , cart.fractionDigits);
        _metaData['revenue_dec'] = formatCurrencyForGa(_metaData.revenue, cart.fractionDigits);
        _metaData['shipping_dec'] = formatCurrencyForGa(_metaData.shipping, cart?.fractionDigits);
        const _fbMetaData = {
            'event_name': 'InitiateCheckout'
        };
        dispatchGaAndFacebookEvent(_metaData, _fbMetaData, currentPage);
    }
    catch (e) {
        console.log(e);
    }
}

export const updateProductHistoryOnItemDelete = (cart, mainItemId, type) => {
    let products: any = [];
    try {
        for (let x of Object.keys(cart.lineItems)) {
            if (x === mainItemId) {
                for (let i = 0; i < cart.lineItems[x].length; i++) {
                    if (cart.lineItems[x][i].type === type) {
                        let attributes: any = [];
                        const userSelectionInfo = cart?.lineItems[x]?.[0]?.data?.productAttributes?.userSelectionInfo || [];
                        const isProductOOS = cart?.lineItems[x]?.[0]?.data?.isOutOfStock || false;
                        let currentlineItemId = getCurrentLineItemId(cart, x, i);
                        const templateKey = cart?.lineItems[x]?.[0]?.data?.customfield?.templateKey || '';
                        attributes = setAttributes(userSelectionInfo,isProductOOS,currentlineItemId,templateKey);
                        let product: any = {
                            accessories: cart.lineItems[x][i].type === 'accessory' ? 1 : 0,
                            attributes: attributes,//lineitem.productAttributes.userSelectionInfo.[originalAttributeValue,originalAttributeKey]
                            class: cart.lineItems[x][i].type === 'accessory' ? cart.lineItems[x][i].data.productFamilyName : cart.lineItems[x][0].data.webCode,//lineitem.webCode
                            logo: (cart.lineItems[x][i].type === 'upsell' || cart.lineItems[x][i].type === 'accessory') ? 0 : window.np.getPriceValue(currencyFormatter(cart.lineItems[x][0].data.itemLogoCharge, cart.fractionDigits)),//lineitem.itemLogoCharge
                            name: cart.lineItems[x][i].type === 'upsell' ? cart.lineItems[x][0].data.name : cart.lineItems[x][i].data.name,//lineitem.name
                            product_type: (cart.lineItems[x][i].type === 'upsell' || cart.lineItems[x][i].type === 'accessory') ? cart.lineItems[x][0].data.productType : cart.lineItems[x][i].data.productType,
                            price: window.np.getPriceValue(currencyFormatter(cart.lineItems[x][i].data.unitPrice, cart.fractionDigits)).replace(/ /g,""),
                            price_total: '-'+window.np.getPriceValue(currencyFormatter(cart.lineItems[x][i].data.totalPriceWithoutAnyCharges, cart.fractionDigits)),
                            qty: cart.lineItems[x][i].type === 'mainitem' ? cart.lineItems[x][i].data.totalQuantityWithSubItem : cart.lineItems[x][i].data.quantity,
                            qty_free: cart?.lineItems[x][0]?.data?.free_qty ? cart.lineItems[x][0].data.free_qty : 0,
                            setup: (cart.lineItems[x][i].type === 'upsell' || cart.lineItems[x][i].type === 'accessory') ? 0 : window.np.getPriceValue(currencyFormatter(cart.lineItems[x][0].data.lineItemSetupCharge,cart.fractionDigits)),//lineitem.lineItemSetupCharge
                            sku: cart.lineItems[x][0].data.mcpSku,//lineitem.mcpSku
                            tax: config('isNASite') ? window.np.getPriceValue(currencyFormatter(0, cart.fractionDigits)) : (window.np.getPriceValue(currencyFormatter(cart.lineItems[x][i].data.totalPriceWithoutAnyCharges * cart.lineItems[x][0].data.taxRate, cart.fractionDigits))),
                            upsell: cart.lineItems[x][i].type === 'upsell' ? 1 : 0,
                            ...getSliderInfoFromLS(cart.lineItems[x][i]?.data?.id),
                            ...addMerchantCategory(cart.lineItems[x][i]?.data?.id),
                            ...addPCCFlag(cart?.lineItems[x][i]?.data?.postCheckOutCustomization),
                            ...addImportFeeForGa(cart?.lineItems[x][i], cart?.fractionDigits)
                        };
                        unsetSliderInfoToLS(cart.lineItems[x][i]?.data?.id);
                        const currentItem = cart?.lineItems[x][i];
                        const itemData = currentItem?.data;
                        const itemType = currentItem?.type;
                        product.coupon = getCouponValue(currentItem, cart);

                        switch (itemType) {
                            case 'mainitem':
                                product = {...product, ...getCstmzrTypeInfoFromLS(itemData?.id)};
                                product.reorder = itemData?.customfield?.lineItemOrderType === ReOrder.REORDER_LINEITEM_ORDERTYPE ? 1 : 0;
                                product.original_price = getDecimalValueForGa(cart.lineItems[x][0].data.baseUnitPrice || 0, cart?.fractionDigits);
                                product.discount = Number(((itemData.totalDiscountWithSubItem / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits));
                                break;
                            case 'upsell':
                                product = {...product, ...getCstmzrTypeInfoFromLS(itemData?.mainItemId)};
                                product.discount = Number(((itemData.totalDiscount / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits));
                                break;
                            case 'accessory':
                                product.discount = Number(((itemData.totalDiscount / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits));
                                break;
                            default:
                                break;
                        }
                        product['price'] = formatCurrencyForGa(product?.price || 0, cart?.fractionDigits);
                        product['logo_dec'] = formatCurrencyForGa(product?.logo || 0, cart?.fractionDigits);
                        product['setup_dec'] = formatCurrencyForGa(product?.setup || 0, cart?.fractionDigits);
                        products.push(product)
                    }
                }
            }
        }
        var _metaData = {
            'event': 'cart_remove',
            'products': products,
            ...(type !== 'accessory' && getCstmzrTypeInfoFromLS(mainItemId,true)),
            ...(type === 'mainitem' && addPathAndPosition(mainItemId)),
            ...addPCCFlag(cart?.hasPCCItem),
        };
        sendGaEvent(_metaData);
        if(type==='mainitem'){
            window.pdi.localStorage.removeOne(mainItemId);
        }
    } catch (e) {
        console.log(e)
    }
}

export const updateGaCartView = (cart,coupon,isShippingDisc) => {
    let products: any = [];
    if(cart.lineItems){
        try {
            let revenue = currencyFormatter(cart.cartSubtotal, cart.fractionDigits);
            const { isThresholdMet } = calculateThresholdStatus(cart, coupon?.thresholdValue);
            const doesDiscountExist = (cart?.discountExists || (!cart?.shippingCustomItem && coupon?.thresholdValue && isThresholdMet && isShippingDisc) || (!cart?.shippingCustomItem && !coupon?.thresholdValue && isShippingDisc)) ?? false;
            let cartTotalSaving = cart.customerDiscountCoupon;
            let tax = config('isNASite') ? currencyFormatter(0, cart.fractionDigits) : currencyFormatter(cart.VAT, cart.fractionDigits);
            for (let x of Object.keys(cart.lineItems)) {
                for (let i = 0; i < cart.lineItems[x].length; i++) {
                    if (cart.lineItems[x][i].type !== 'free' && cart.lineItems[x][i].type !== 'subitem' && cart.lineItems[x][i].type !== 'additionalcharge') {
                        let attributes: any = [];
                        if(cart.lineItems[x][i].type !== 'accessory') {
                            const userSelectionInfo = cart?.lineItems[x]?.[0]?.data?.productAttributes?.userSelectionInfo || [];
                            const isProductOOS = cart?.lineItems[x]?.[0]?.data?.isOutOfStock || false;
                            let currentlineItemId = getCurrentLineItemId(cart, x, i);
                            const templateKey = cart?.lineItems[x]?.[0]?.data?.customfield?.templateKey || '';
                            attributes = setAttributes(userSelectionInfo,isProductOOS,currentlineItemId,templateKey);
                        }
                        let product: any = {
                            accessories: cart.lineItems[x][i].type === 'accessory' ? 1 : 0,
                            attributes: attributes,//lineitem.productAttributes.userSelectionInfo.[originalAttributeValue,originalAttributeKey]
                            class: cart.lineItems[x][i].type === 'accessory' ? cart.lineItems[x][i].data.productFamilyName : cart.lineItems[x][0].data.webCode,//lineitem.webCode
                            logo: (cart.lineItems[x][i].type === 'upsell' || cart.lineItems[x][i].type === 'accessory') ? 0 : window.np.getPriceValue(currencyFormatter(cart.lineItems[x][0].data.itemLogoCharge, cart.fractionDigits)),//lineitem.itemLogoCharge
                            name: cart.lineItems[x][i].type === 'upsell' ? cart.lineItems[x][0].data.name : cart.lineItems[x][i].data.name,//lineitem.name
                            product_type: (cart.lineItems[x][i].type === 'upsell' || cart.lineItems[x][i].type === 'accessory') ? cart.lineItems[x][0].data.productType : cart.lineItems[x][i].data.productType,
                            price: window.np.getPriceValue(currencyFormatter(cart.lineItems[x][i].data.unitPrice, cart.fractionDigits)),
                            qty: cart.lineItems[x][i].type === 'mainitem' ? cart.lineItems[x][i].data.totalQuantityWithSubItem : cart.lineItems[x][i].data.quantity,//lineitem.quantity
                            qty_free: cart?.lineItems[x][0]?.data?.free_qty ? cart.lineItems[x][0].data.free_qty : 0,
                            setup: (cart.lineItems[x][i].type === 'upsell' || cart.lineItems[x][i].type === 'accessory') ? 0 : window.np.getPriceValue(currencyFormatter(cart.lineItems[x][0].data.lineItemSetupCharge,cart.fractionDigits)),//lineitem.lineItemSetupCharge
                            sku: cart.lineItems[x][0].data.mcpSku,//lineitem.mcpSku
                            upsell: cart.lineItems[x][i].type === 'upsell' ? 1 : 0,
                            ...addProofAndCommentForUsg(cart?.lineItems[x][i]),
                            ...addPathAndPosition(cart?.lineItems[x][i]?.data?.id),
                            ...addMerchantCategory(cart.lineItems[x][i]?.data?.id),
                            ...getSliderInfoFromLS(cart.lineItems[x][i]?.data?.id),
                            ...addPCCFlag(cart?.lineItems[x][i]?.data?.postCheckOutCustomization),
                            ...addImportFeeForGa(cart?.lineItems[x][i], cart?.fractionDigits)
                        };
                        const currentItem = cart?.lineItems[x][i];
                        const itemData = currentItem?.data;
                        const itemType = currentItem?.type;
                        product.coupon = getCouponValue(currentItem, cart);

                        switch (itemType) {
                            case 'mainitem':
                                product = {...product, ...getCstmzrTypeInfoFromLS(itemData?.id)};
                                product.reorder = itemData?.customfield?.lineItemOrderType === ReOrder.REORDER_LINEITEM_ORDERTYPE ? 1 : 0;
                                product.original_price = getDecimalValueForGa(cart.lineItems[x][0].data.baseUnitPrice || 0, cart?.fractionDigits);
                                product.discount = Number(((itemData.totalDiscountWithSubItem / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits));
                                break;
                            case 'upsell':
                                product = {...product, ...getCstmzrTypeInfoFromLS(itemData?.mainItemId)};
                                product.discount = Number(((itemData.totalDiscount / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits));
                                break;
                            case 'accessory':
                                product.discount = Number(((itemData.totalDiscount / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits));
                                break;
                            default:
                                break;
                        }
                        product['price'] = formatCurrencyForGa(product?.price || 0, cart?.fractionDigits);
                        product['logo_dec'] = formatCurrencyForGa(product?.logo || 0, cart?.fractionDigits);
                        product['setup_dec'] = formatCurrencyForGa(product?.setup || 0, cart?.fractionDigits);
                        products.push(product)
                    }
                }
            }
            var _metaData = {
                event: 'cart_view',
                products: products,
                revenue: window.np.getPriceValue(revenue),
                coupon: (doesDiscountExist && cartTotalSaving === coupon.code) ? cartTotalSaving : "NA",
                tax: window.np.getPriceValue(tax) || 0,
                ...addPCCFlag(cart?.hasPCCItem),
                ...getTotalImportFeeForGa(cart),
                tax_dec: formatCurrencyForGa(window.np.getPriceValue(tax) || 0, cart?.fractionDigits),
                orderDiscount: cart?.cartTotalSaving ? 
                getPriceValue(
                    currencyFormatter(cart?.cartTotalSaving, cart?.fractionDigits)
                ).replace(/ /g,"") : 0, 
            };
            _metaData['revenue_dec'] = formatCurrencyForGa(_metaData.revenue, cart?.fractionDigits);
            sendGaEvent(_metaData);
        } catch (e) {
            console.log(e);
        }
    }
}

export const tierSelectGaEvent = (tierData, index, isMultiselect,mainItem, action, fractionDigits) => {
    try {
        if(isMultiselect){
            let _metaData = {
                event: 'tier_select',
                qty: tierData.qty,
                price: formatCurrencyForGa(tierData.price || 0, fractionDigits),
                pos: index +1,
                web_code : mainItem?.webCode || mainItem?.productFamilyName,
                action: action
            };
            sendGaEvent(_metaData);
        }
        else{
            let _metaData = {
                event: 'tier_select',
                qty: tierData.quantity,
                free: tierData.freeQuantity,
                price: formatCurrencyForGa(tierData.unitPriceWithoutCurrency || 0, fractionDigits),
                pos: index + 1,
                web_code : mainItem?.webCode || mainItem?.productFamilyName,
                action: action
            };
            sendGaEvent(_metaData);
        }
        
    } catch (e) {
        console.log(e);
    }
}

export const updateProductHistoryOnUpsellUpdate = (cart, mainItemId) => {
    let products: any = [];
    try {
        for (let x of Object.keys(cart.lineItems)) {
            if (x === mainItemId) {
                for (let i = 0; i < cart.lineItems[x].length; i++) {
                    if (cart.lineItems[x][i].type === 'upsell') {
                        let attributes: any = [];
                        for (let j = 0; j < cart.lineItems[x][0].data.productAttributes.userSelectionInfo.length; j++) {
                            let attr = {
                                key: cart.lineItems[x][0].data.productAttributes.userSelectionInfo[j].originalAttributeKey,
                                value: cart.lineItems[x][0].data.productAttributes.userSelectionInfo[j].multiDisplayValue ? cart.lineItems[x][0].data.productAttributes.userSelectionInfo[j].multiDisplayValue.map(val => val.split("-")[0]) : cart.lineItems[x][0].data.productAttributes.userSelectionInfo[j].originalAttributeValue
                            }
                            attributes.push(attr);
                        } 
                        let product = {
                            accessories: 0,
                            attributes: attributes,//lineitem.productAttributes.userSelectionInfo.[originalAttributeValue,originalAttributeKey]
                            category: cart.lineItems[x][0].data.categoryName,//lineitem.categoryName
                            class: cart.lineItems[x][0].data.webCode,//lineitem.webCode
                            logo: 0,//lineitem.itemLogoCharge
                            name: cart.lineItems[x][0].data.name,//lineitem.name
                            product_type: cart.lineItems[x][0].data.productType ? cart.lineItems[x][0].data.productType : '',
                            price: window.np.getPriceValue(currencyFormatter(cart.lineItems[x][0].data.unitPrice, cart.fractionDigits)),
                            price_total: window.np.getPriceValue(currencyFormatter(cart.lineItems[x][i].data.totalPriceWithoutAnyCharges, cart.fractionDigits)),
                            qty: cart.lineItems[x][i].data.quantity,//lineitem.quantity
                            qty_free: 0,//lineitem.usertier.freeQuantity
                            setup: 0,//lineitem.lineItemSetupCharge
                            sku: cart.lineItems[x][0].data.mcpSku,//lineitem.mcpSku
                            tax: config('isNASite') ? window.np.getPriceValue(currencyFormatter(0, cart?.fractionDigits)) : (window.np.getPriceValue(currencyFormatter(cart?.lineItems[x][i]?.data?.totalPriceWithoutAnyCharges * cart.lineItems[x][0]?.data?.taxRate, cart?.fractionDigits))),
                            upsell: 1,
                            discount : Number(((cart.lineItems[x][i].data.totalDiscount / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits)),
                            ...getCstmzrTypeInfoFromLS(mainItemId),
                            ...getListId(cart.lineItems[x][i]?.data?.id),
                            ...addMerchantCategory(cart.lineItems[x][i]?.data?.id),
                            ...getSliderInfoFromLS(cart.lineItems[x][i]?.data?.id),
                            ...addPCCFlag(cart?.lineItems[x][i]?.data?.postCheckOutCustomization),
                            ...addImportFeeForGa(cart?.lineItems[x][i], cart?.fractionDigits)
                        };
                        product['price'] = formatCurrencyForGa(product?.price || 0, cart?.fractionDigits);
                        product.coupon = getCouponValue(cart?.lineItems[x][i], cart);
                        products.push(product)
                    }
                }
            }
        }
        var _metaData = {
            'event': 'cart_add',
            'products': products,
            ...getCstmzrTypeInfoFromLS(mainItemId,true),
        };
        sendGaEvent(_metaData);
    } catch (e) {
        console.log(e)
    }
}

export const updateProductHistoryOnQuantityUpdate = (cart, mainItemId, itemType, currentPage) => {
    let products: any = [];
    let priceTotalFb: any;
    try {
        for (let x of Object.keys(cart.lineItems)) {
            if (x === mainItemId) {
                for (let i = 0; i < cart.lineItems[x].length; i++) {
                    if (cart.lineItems[x][i].type === itemType) {
                        let attributes: any = [];

                        const userSelectionInfo = cart?.lineItems[x]?.[0]?.data?.productAttributes?.userSelectionInfo || [];
                        const isProductOOS = cart?.lineItems[x]?.[0]?.data?.isOutOfStock || false;
                        let currentlineItemId = getCurrentLineItemId(cart, x, i);
                        const templateKey = cart?.lineItems[x]?.[0]?.data?.customfield?.templateKey || '';
                        attributes = setAttributes(userSelectionInfo,isProductOOS,currentlineItemId,templateKey);

                        let product : any = {
                            accessories: cart.lineItems[x][i].type === 'accessory' ? 1 : 0,
                            attributes: attributes,//lineitem.productAttributes.userSelectionInfo.[originalAttributeValue,originalAttributeKey]
                            class: cart.lineItems[x][i].type === 'accessory' ? cart.lineItems[x][i].data.productFamilyName : cart.lineItems[x][0].data.webCode,//lineitem.webCode
                            logo: window.np.getPriceValue(currencyFormatter(cart.lineItems[x][0].data.itemLogoCharge, cart.fractionDigits)),//lineitem.itemLogoCharge
                            name: cart.lineItems[x][i].type === 'upsell' ? cart.lineItems[x][0].data.name : cart.lineItems[x][i].data.name,//lineitem.name
                            product_type: (cart.lineItems[x][i].type === 'upsell' || cart.lineItems[x][i].type === 'accessory') ? cart.lineItems[x][0].data.productType : cart.lineItems[x][i].data.productType,
                            price: window.np.getPriceValue(currencyFormatter(cart.lineItems[x][i].data.unitPrice, cart.fractionDigits)),
                            price_total: cart.lineItems[x][i].data?.multiSelectData?.isExist === false ? window.np.getPriceValue(currencyFormatter(cart.lineItems[x][i].data.totalPriceWithoutAnyCharges, cart.fractionDigits)) : window.np.getPriceValue(currencyFormatter(cart.lineItems[x][i].data.totalPriceWithoutAnyCharges, cart.fractionDigits)),
                            qty: cart.lineItems[x][i].data.quantity,//lineitem.quantity
                            qty_free: cart?.lineItems[x][0]?.data?.free_qty ? cart.lineItems[x][0].data.free_qty : 0,
                            setup:  window.np.getPriceValue(currencyFormatter(cart.lineItems[x][0].data.lineItemSetupCharge,cart.fractionDigits)),//lineitem.lineItemSetupCharge
                            sku: cart.lineItems[x][0].data.mcpSku,//lineitem.mcpSku
                            tax: config('isNASite') ? window.np.getPriceValue(currencyFormatter(0, cart?.fractionDigits)) : (window.np.getPriceValue(currencyFormatter(cart?.lineItems[x][i]?.data?.totalPriceWithoutAnyCharges * cart.lineItems[x][0]?.data?.taxRate, cart?.fractionDigits))),
                            upsell: 0,
                            ...getCstmzrTypeInfoFromLS(mainItemId),
                            ...addProofAndCommentForUsg(cart?.lineItems[x][i]),
                            ...getSliderInfoFromLS(cart.lineItems[x][i]?.data?.id),
                            ...addMerchantCategory(cart.lineItems[x][i]?.data?.id),
                            ...addImportFeeForGa(cart?.lineItems[x][i], cart?.fractionDigits)
                        };
                        if (cart?.lineItems[x][i]?.type === 'mainitem') {
                            product.reorder = cart?.lineItems[x][i]?.data?.customfield?.lineItemOrderType === ReOrder.REORDER_LINEITEM_ORDERTYPE ? 1 : 0;
                            product.original_price = getDecimalValueForGa(cart.lineItems[x][0].data.baseUnitPrice || 0, cart?.fractionDigits);
                            product.discount = Number(((cart?.lineItems[x][i]?.data?.totalDiscountWithSubItem / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits));
                        }
                        product.coupon = getCouponValue(cart?.lineItems[x][i], cart);
                        product['price'] = formatCurrencyForGa(product?.price || 0, cart?.fractionDigits);
                        product['logo_dec'] = formatCurrencyForGa(product?.logo || 0, cart?.fractionDigits);
                        product['setup_dec'] = formatCurrencyForGa(product?.setup || 0, cart?.fractionDigits);
                        
                        priceTotalFb = product?.price_total;
                        products.push(product)
                    }
                }
            }
        }
        var _metaData = {
            'event': 'cart_add',
            'products': products,
            ...addPathAndPosition(mainItemId),
            ...getCstmzrTypeInfoFromLS(mainItemId,true),
        };
        const _fbMetaData = {
            'event_name': 'AddToCart',
            'custom_data': {
                "content_type":"product",
                "content_ids" : _metaData.products.map(product => product.class),
                "value": priceTotalFb,
                "currency": window.currency || ''
            }
        };
        
        if (itemType === 'mainitem') {
            dispatchGaAndFacebookEvent(_metaData, _fbMetaData, currentPage)
        } 
        else {
            sendGaEvent(_metaData);
        }
        
    } catch (e) {
        console.log(e)
    }
}

export const updateProductHistoryOnAddAccessory = (cart, mainItemId, itemType, currentPage, itemId) => {
    let products: any = [];
    let priceTotalFb: any;
    try {
        for (let x of Object.keys(cart?.lineItems)) {
            if (x === mainItemId) {
                for (let i = 0; i < cart?.lineItems[x]?.length; i++) {
                    if (cart?.lineItems[x][i]?.type === itemType && cart?.lineItems[x][i]?.data?.productId === itemId) {
                        const mainItem = cart?.lineItems[x][0];
                         
                        let product : any = {
                            accessories: 1,
                            attributes: [],//lineitem.productAttributes.userSelectionInfo.[originalAttributeValue,originalAttributeKey]
                            category: mainItem?.data?.categoryName,//lineitem.categoryName
                            class: cart?.lineItems[x][i]?.type === 'accessory' ? cart?.lineItems[x][i]?.data?.productFamilyName : mainItem?.data?.webCode,//lineitem.webCode
                            logo: 0,//lineitem.itemLogoCharge
                            name: cart?.lineItems[x][i]?.type === 'upsell' ? mainItem?.data?.name : cart?.lineItems[x][i]?.data?.name,//lineitem.name
                            product_type: (cart?.lineItems[x][i]?.type === 'upsell' || cart?.lineItems[x][i]?.type === 'accessory') ? cart.lineItems[x][0].data.productType : cart.lineItems[x][i].data.productType,
                            price: window.np.getPriceValue(currencyFormatter(cart?.lineItems[x][i]?.data?.unitPrice, cart?.fractionDigits)),
                            price_total: cart?.lineItems[x][i]?.data?.multiSelectData?.isExist === false ? window.np.getPriceValue(currencyFormatter(cart?.lineItems[x][i]?.data?.totalPriceWithoutAnyCharges, cart?.fractionDigits)) : window.np.getPriceValue(currencyFormatter(cart?.lineItems[x][i]?.data?.totalPriceWithoutAnyCharges, cart?.fractionDigits)),
                            qty: cart?.lineItems[x][i]?.data?.quantity,//lineitem.quantity
                            qty_free: cart?.lineItems[x][0]?.data?.free_qty ? cart.lineItems[x][0].data.free_qty : 0,
                            setup: 0,//lineitem.lineItemSetupCharge
                            sku: mainItem?.data?.mcpSku,//lineitem.mcpSku
                            tax: config('isNASite') ? window.np.getPriceValue(currencyFormatter(0, cart?.fractionDigits)) : (window.np.getPriceValue(currencyFormatter(cart?.lineItems[x][i]?.data?.totalPriceWithoutAnyCharges * mainItem?.data?.taxRate, cart?.fractionDigits))),
                            upsell: 0,
                            discount: Number(((cart?.lineItems[x][i]?.data?.totalDiscount / Math.pow(10, cart.fractionDigits)) || 0)?.toFixed(cart.fractionDigits)),
                            ...getListId(cart.lineItems[x][i]?.data?.id),
                            ...addMerchantCategory(cart.lineItems[x][i]?.data?.id),
                            ...getSliderInfoFromLS(cart.lineItems[x][i]?.data?.id),
                            ...addImportFeeForGa(cart?.lineItems[x][i], cart?.fractionDigits)
                        };
                        product['price'] = formatCurrencyForGa(product?.price || 0, cart?.fractionDigits);
                        product.coupon = getCouponValue(cart.lineItems[x][i], cart);
                        priceTotalFb = product?.price_total;
                        products.push(product)
                    }
                }
            }
        }
        var _metaData = {
            'event': 'cart_add',
            'products': products,
        };
        sendGaEvent(_metaData);
    } catch (e) {
        console.log(e)
    }
}

const getListId = (lineitemId: string) => {
    if (!lineitemId) return {};

    const pdi = window.pdi.localStorage.get();
    return pdi[lineitemId]
    ? { listId: pdi[lineitemId]?.listId }
    : { listId: "" };
};

export const addPathAndPosition = (lineitemId) => {
    if (lineitemId) {
        const PdiObj = window.pdi.localStorage.get();
        if (PdiObj?.[lineitemId]) {
            const { listId, position } = PdiObj[lineitemId];
            if (listId && listId.includes('[Int-Promo]')) {
                const promoName = listId.split('_')[2] || ""; 
                return {
                    listId,
                    position,
                    promotion_id: listId,
                    promo_name: promoName,
                };
            }
            return { listId, position };
        }
        return { listId: "", position: -1 };
    }
    return {};
  }; 
export const addPCCFlag = (isPCC) => {

    if(config('locale') === 'en-CA'){
        return { 
            pcc : isPCC ? true : false
        }
    }
    return {};
};
const addMerchantCategory = (lineitemId) => {
    if (lineitemId) {
      const PdiObj = window.pdi.localStorage.get();
      return PdiObj && Object.keys(PdiObj).length && PdiObj[lineitemId]
        ? { merch_cat_key: PdiObj[lineitemId]?.merchCatKey, merch_cat: PdiObj[lineitemId]?.merchCat }
        : { merch_cat_key: 'Not-Applicable', merch_cat: 'Not-Applicable' };
    }
    return {};
};
const addProofAndCommentForUsg = (lineItem) => {
    const isUsg = lineItem?.data?.customizationInfo?.isUsgUIProduct === true;
    if(lineItem?.type === 'mainitem' && isUsg){
        return {
            'proof' : lineItem?.data?.digitalPrintProofRequired ? 1 : 0,
            'comment' : lineItem?.data?.specialInstructionsForLineItem ? 1 : 0
        }
    }
    return {};
 }

 export const cartAddOnReorder = (lineItem, isHomePageReorder=false) => {
    let products: any = [];
    try {
        let attributes: any = [];
        for (let j = 0; j < lineItem?.productDetailsData?.userSelectionInfo?.length; j++) {
            let attr = {
                key:  lineItem?.productDetailsData?.userSelectionInfo[j]?.attribute,
                value:  lineItem?.productDetailsData?.userSelectionInfo[j]?.multiDisplayValue ? lineItem?.productDetailsData?.userSelectionInfo[j]?.multiDisplayValue.map(val => val.split("-")[0]) : lineItem?.productDetailsData?.userSelectionInfo[j]?.value 
            }
            attributes.push(attr);
        }

        const pdi = window.pdi.sessionStorage.get();
        const reorderInfo = isHomePageReorder
        ? { listId: pdi?.listId, promo_name: pdi?.promo_name, promotion_id: pdi?.promotion_id }
        : { listId: '[CAM]My Account_Reorder', promo_name: null, promotion_id: null };

        let product = {
            accessories: 0,
            attributes: attributes,
            category: lineItem?.categoryName || '',
            class: lineItem?.productFamilyName,
            logo: lineItem?.itemLogoCharge || '',
            name: lineItem?.productName,
            product_type: lineItem?.productType || '',
            price: lineItem?.itemUnitPrice,
            price_total: lineItem?.itemSubtotal,
            qty: lineItem?.quantity,
            qty_free: 0, 
            setup: lineItem?.itemSetupCharge || 0,
            sku: lineItem?.mcpSku,
            comments: lineItem?.specialInstructionsForLineItem,
            proof: lineItem?.digitalPrintProofRequired,
            reorder: 1,
            upsell: 0,
            ...getSliderInfoFromLS(lineItem?.data?.id),
            ...reorderInfo
        };
        products.push(product)
        var _metaData = {
            'event': 'cart_add_dimp',
            'products': products
        };
        sendGaEvent(_metaData);
    } catch (e) {
        console.log(e)
    }
}

export const customizeLaterInteractionGa = (label) => {
    try{
        var _metaData = {
            'event': CustomizeLater.CUSTOMIZE_LATER_INTERACTION,
            'label': label
        };
        sendGaEvent(_metaData);
    }
    catch(e){
        console.log(e)
    }
}

export const addReorderItemToCartGa = (cart, mainItemId) => {
    let products: any = [];
    try {
        for (let lineitemId of Object.keys(cart?.lineItems)) {
            if (lineitemId === mainItemId) {
                for (let i = 0; i < cart?.lineItems[lineitemId]?.length; i++) {
                    if (cart?.lineItems[lineitemId][i]?.type === 'mainitem') {
                        let attributes: any = [];
                        const userSelectionInfo = cart?.lineItems[lineitemId]?.[0]?.data?.productAttributes?.userSelectionInfo || [];
                        const isProductOOS = cart?.lineItems[lineitemId]?.[0]?.data?.isOutOfStock || false;
                        let currentlineItemId = getCurrentLineItemId(cart, lineitemId, i);
                        const templateKey = cart?.lineItems[lineitemId]?.[0]?.data?.customfield?.templateKey || '';
                        attributes = setAttributes(userSelectionInfo,isProductOOS,currentlineItemId,templateKey);
                        let product : any = {
                            accessories: 0,
                            attributes: attributes,
                            class: cart?.lineItems[lineitemId][0]?.data?.webCode,
                            logo: window.np.getPriceValue(currencyFormatter(cart?.lineItems[lineitemId][0]?.data?.itemLogoCharge, cart?.fractionDigits)),
                            name: cart?.lineItems[lineitemId][i]?.data?.name,
                            product_type: cart?.lineItems[lineitemId][i]?.data?.productType,
                            price: cart?.lineItems[lineitemId][i]?.data?.unitPrice,
                            price_total: cart?.lineItems[lineitemId][i]?.data?.multiSelectData?.isExist === false ? window.np.getPriceValue(currencyFormatter(cart?.lineItems[lineitemId][i]?.data?.totalPriceWithoutAnyCharges, cart?.fractionDigits)) : window.np.getPriceValue(currencyFormatter(cart?.lineItems[lineitemId][i]?.data.totalPriceWithoutAnyCharges, cart?.fractionDigits)),
                            qty: cart?.lineItems[lineitemId][i]?.data?.quantity,
                            qty_free: cart?.lineItems[lineitemId][0]?.data?.free_qty ? cart.lineItems[lineitemId][0].data.free_qty : 0,
                            setup:  window.np.getPriceValue(currencyFormatter(cart?.lineItems[lineitemId][0]?.data?.lineItemSetupCharge,cart?.fractionDigits)),
                            sku: cart.lineItems[lineitemId][0].data.mcpSku,
                            tax: config('isNASite') ? window.np.getPriceValue(currencyFormatter(0, cart?.fractionDigits)) : (window.np.getPriceValue(currencyFormatter(cart?.lineItems[lineitemId][i]?.data?.totalPriceWithoutAnyCharges * cart.lineItems[lineitemId][0]?.data?.taxRate, cart?.fractionDigits))),
                            upsell: 0,
                            reorder: cart?.lineItems[lineitemId][i]?.data?.customfield?.lineItemOrderType === ReOrder.REORDER_LINEITEM_ORDERTYPE ? 1 : 0,
                            ...addPathAndPosition(mainItemId),
                            ...getCstmzrTypeInfoFromLS(mainItemId),
                            ...addProofAndCommentForUsg(cart?.lineItems[lineitemId][i]),
                            ...getSliderInfoFromLS(cart?.lineItems[lineitemId][i]?.data?.id),
                            ...addMerchantCategory(cart?.lineItems[lineitemId][i]?.data?.id),
                            ...addImportFeeForGa(cart?.lineItems[lineitemId][i], cart?.fractionDigits)
                        };                        
                        product['price'] = formatCurrencyForGa(product?.price || 0, cart?.fractionDigits);
                        product['logo_dec'] = formatCurrencyForGa(product?.logo || 0, cart?.fractionDigits);
                        product['setup_dec'] = formatCurrencyForGa(product?.setup || 0, cart?.fractionDigits);
                        
                        products.push(product)
                    }
                }
            }
        }
        var _metaData = {
            'event': 'cart_add',
            'products': products,
            //Add isEventLevel is true when customizer types changes are pushed
            ...getCstmzrTypeInfoFromLS(mainItemId),
        };
        sendGaEvent(_metaData);      
    } catch (e) {
        console.log(e)
    }
}

export const reorderImpressionEvent = (reorderProducts) => {
    try {
      if (!reorderProducts || !reorderProducts.length) return;
  
      const products = reorderProducts.map((product, index) => ({
        position: index + 1,
        class: product?.productFamilyName,
        name: product?.productName,
        listId: "[PR]Cart Page_Reorder",
      }));
  
      const _metaData = {
        event: 'product_impression',
        products,
        listId: "[PR]Cart Page_Reorder",
      };
  
      sendGaEvent(_metaData);
    } catch (e) {
      console.error(e);
    }
  };

export const dispatchProdRecCarouselEvent = (label) =>{
    try{
        var _metaData = {
        'event': 'prod_recom_carousel',
        'label': label,
        'event_subname': 'Cart Page_Reorder'
        };
    sendGaEvent(_metaData);      
    } catch (e) {
        console.log(e)
    }
}

/**
 * Returns import fee object for GA events based on locale and line item data
 * Returns an object with import_fee key for 'en-US' locale or an empty object for other locales
 */
export const addImportFeeForGa = (lineItem, fractionDigits) => {
    const isUSLocale = config('locale') === 'en-US';
    if (!isUSLocale) return {};
    
    const hasImportCharge = lineItem?.type === 'mainitem' && lineItem?.data?.itemImportCharge;
    if (!hasImportCharge) return { import_fee: 0 };

    const formattedPrice = currencyFormatter(lineItem.data.itemImportCharge, fractionDigits);
    const importFee = window?.np?.getPriceValue?.(formattedPrice);

    return { import_fee: isNaN(importFee) ? 0 : importFee };
};

/**
 * Calculates the total import fees for all line items in the cart for GA events
 * Returns an object with import_fee key for 'en-US' locale or {} for other locales
 */
export const getTotalImportFeeForGa = (cart: Cart): { import_fee?: number } => {
    try {
        const isUSLocale = config('locale') === 'en-US';
        if (!isUSLocale) return {};
        
        let totalImportFee = 0;
        
        if (cart?.lineItems) {
            for (let lineItemId of Object.keys(cart.lineItems)) {
                for (let i = 0; i < cart.lineItems[lineItemId].length; i++) {
                    const lineItem = cart.lineItems[lineItemId][i];
                    if (lineItem?.type === 'mainitem' && lineItem?.data?.itemImportCharge) {
                    const formattedPrice = currencyFormatter(lineItem.data.itemImportCharge, cart.fractionDigits);
                    const importFee = window?.np?.getPriceValue?.(formattedPrice);
                        if (!isNaN(importFee)) {
                            totalImportFee += parseFloat(importFee);
                        }
                    }
                }
            }
        }
        return { import_fee: totalImportFee };
    } catch (error) {
        console.error('Error calculating total import fee:', error);
        return {};
    }
};
